// Single-producer / single-consumer lock-free rings (P0.3).
//
// Two policies, because the two rings want opposite things when they fill:
//
//   reject-and-count (commands)  the writer refuses and counts. Dropping a fader move the user
//                                made is worse than admitting we dropped it.
//   overwrite-oldest (signals)   the writer never stops. The renderer wants the newest beat,
//                                not a backlog of stale ones.
//
// Rules this file lives by, all of them from Principle 1 ("audio is sacred"):
//
//   * Nothing blocks. No lock, no Atomics.wait, on either side. The audio thread has 2.7 ms
//     and no slack; anything it waits on is an audible glitch.
//   * Nothing allocates. Views are built once at construction; the read and write paths do
//     arithmetic and typed-array access only. A GC pause on the audio thread is a click.
//   * Sequences are stored as int32 bit patterns, not uint32 numbers. Same bits, but a uint32
//     past 2^31 leaves V8's small-integer range and becomes a heap number — an allocation, on
//     the path that must not allocate. `sequenceDistance` reads either representation
//     correctly because it coerces the *difference*, which is always small.
//
// Correctness rests on one pairing: the producer's plain writes into a slot, then
// `Atomics.store` of the sequence; the consumer's `Atomics.load` of that sequence, then its
// plain reads. The sequentially-consistent pair is what makes the slot's contents visible.
// Reordering those two lines in either class silently breaks the ring on real hardware.

import { asSequence, type ByteOffset } from '../types/branded';
import {
  int32IndexOf,
  RING_HEADER,
  sequenceDistance,
  slotByteOffset,
  slotIndexFor,
  type RingLayout,
} from '../types/ring-layout';
import { assertRingCompatible } from './ring';

/** `claim()` returns this when there is nothing to claim. */
export const NO_SLOT = -1;

const WRITE_SEQ_AT = int32IndexOf(RING_HEADER.WRITE_SEQUENCE);
const READ_SEQ_AT = int32IndexOf(RING_HEADER.READ_SEQUENCE);
const PRODUCER_DROPPED_AT = int32IndexOf(RING_HEADER.PRODUCER_DROPPED);
const CONSUMER_DROPPED_AT = int32IndexOf(RING_HEADER.CONSUMER_DROPPED);

/**
 * How many times the overwrite reader will retry a slot the writer overwrote mid-copy before
 * giving up on that call. A retry only happens when the writer laps the reader during a copy,
 * which needs the reader to be a whole ring behind already. Bounded so a runaway producer can
 * never trap the consumer in a loop.
 */
const MAX_TORN_RETRIES = 4;

// ── Reject-and-count: the command ring ───────────────────────────────────────────────────

/**
 * Producer side. `claim()` reserves the next slot, the caller fills it, `commit()` publishes.
 *
 * The split exists so the caller writes its own record shape without this file knowing what a
 * command looks like, and without a callback — an inline arrow per write would allocate.
 */
export class RejectRingWriter {
  readonly #header: Int32Array;
  readonly #layout: RingLayout;
  /** int32 bit pattern. The producer owns this; nobody else writes WRITE_SEQUENCE. */
  #writeSeq: number;

  constructor(sab: SharedArrayBuffer, layout: RingLayout) {
    assertRingCompatible(sab, layout);
    this.#header = new Int32Array(sab);
    this.#layout = layout;
    this.#writeSeq = Atomics.load(this.#header, WRITE_SEQ_AT);
  }

  /** Byte offset of the slot to fill, or NO_SLOT if the ring is full (and counted). */
  claim(): number {
    const readSeq = Atomics.load(this.#header, READ_SEQ_AT);

    if (
      sequenceDistance(asSequence(readSeq), asSequence(this.#writeSeq)) >=
      this.#layout.capacitySlots
    ) {
      Atomics.add(this.#header, PRODUCER_DROPPED_AT, 1);
      return NO_SLOT;
    }

    return slotByteOffset(this.#layout, asSequence(this.#writeSeq));
  }

  /** Publishes the claimed slot. Must follow a `claim()` that returned an offset. */
  commit(): void {
    this.#writeSeq = (this.#writeSeq + 1) | 0;
    Atomics.store(this.#header, WRITE_SEQ_AT, this.#writeSeq);
  }

  /** Commands refused because the ring was full. */
  get dropped(): number {
    return Atomics.load(this.#header, PRODUCER_DROPPED_AT) >>> 0;
  }
}

/**
 * Consumer side. Reads in place — safe here, and only here, because a reject-policy writer
 * never touches a slot the reader has not yet committed.
 */
export class RejectRingReader {
  readonly #header: Int32Array;
  readonly #layout: RingLayout;
  /** int32 bit pattern. The consumer owns this. */
  #readSeq: number;

  constructor(sab: SharedArrayBuffer, layout: RingLayout) {
    assertRingCompatible(sab, layout);
    this.#header = new Int32Array(sab);
    this.#layout = layout;
    this.#readSeq = Atomics.load(this.#header, READ_SEQ_AT);
  }

  /** Byte offset of the next readable slot, or NO_SLOT when empty. */
  claim(): number {
    const writeSeq = Atomics.load(this.#header, WRITE_SEQ_AT);

    if (writeSeq === this.#readSeq) {
      return NO_SLOT;
    }

    return slotByteOffset(this.#layout, asSequence(this.#readSeq));
  }

  /** Releases the claimed slot back to the writer. Call after reading it. */
  commit(): void {
    this.#readSeq = (this.#readSeq + 1) | 0;
    Atomics.store(this.#header, READ_SEQ_AT, this.#readSeq);
  }

  /** Slots published but not yet read. */
  get pending(): number {
    return sequenceDistance(
      asSequence(this.#readSeq),
      asSequence(Atomics.load(this.#header, WRITE_SEQ_AT)),
    );
  }
}

// ── Overwrite-oldest: the signal ring ────────────────────────────────────────────────────

/**
 * Producer side. Never checks the reader, never refuses, never blocks — which is the entire
 * point. A stalled render worker must not be able to reach back and stall the audio thread.
 */
export class OverwriteRingWriter {
  readonly #header: Int32Array;
  readonly #layout: RingLayout;
  #writeSeq: number;

  constructor(sab: SharedArrayBuffer, layout: RingLayout) {
    assertRingCompatible(sab, layout);
    this.#header = new Int32Array(sab);
    this.#layout = layout;
    this.#writeSeq = Atomics.load(this.#header, WRITE_SEQ_AT);
  }

  /** Byte offset of the slot to fill. Never NO_SLOT — this writer cannot be full. */
  claim(): number {
    return slotByteOffset(this.#layout, asSequence(this.#writeSeq));
  }

  commit(): void {
    this.#writeSeq = (this.#writeSeq + 1) | 0;
    Atomics.store(this.#header, WRITE_SEQ_AT, this.#writeSeq);
  }
}

/**
 * Consumer side. Copies out rather than reading in place, and the copy is not laziness.
 *
 * An overwrite writer will happily rewrite the very slot being read. Reading in place would
 * hand the caller a record half from one frame and half from another — torn, plausible-looking,
 * and untraceable. So: copy, then check whether the writer lapped us during the copy, and only
 * then hand it over. A slot at sequence R is intact exactly while the committed write sequence
 * stays under R + capacity; at R + capacity the writer is inside that slot.
 */
export class OverwriteRingReader {
  readonly #header: Int32Array;
  readonly #layout: RingLayout;
  /** One view per slot, built once. `subarray()` per read would allocate. */
  readonly #slots: readonly Uint8Array[];
  #readSeq: number;

  constructor(sab: SharedArrayBuffer, layout: RingLayout) {
    assertRingCompatible(sab, layout);
    this.#header = new Int32Array(sab);
    this.#layout = layout;
    this.#readSeq = Atomics.load(this.#header, READ_SEQ_AT);

    const slots: Uint8Array[] = [];
    for (let index = 0; index < layout.capacitySlots; index += 1) {
      slots.push(
        new Uint8Array(
          sab,
          layout.payloadByteOffset + index * layout.slotBytes,
          layout.slotBytes,
        ),
      );
    }
    this.#slots = slots;
  }

  /**
   * Copies the oldest intact slot into `destination` (at least `slotBytes` long, allocated once
   * by the caller and reused). Returns false when the ring is empty.
   *
   * Slots the writer overwrote before we reached them are skipped and counted, not reported as
   * errors: falling behind is the policy working, not failing.
   */
  read(destination: Uint8Array): boolean {
    const capacity = this.#layout.capacitySlots;

    for (let attempt = 0; attempt <= MAX_TORN_RETRIES; attempt += 1) {
      const writeSeq = Atomics.load(this.#header, WRITE_SEQ_AT);
      const available = sequenceDistance(asSequence(this.#readSeq), asSequence(writeSeq));

      if (available === 0) {
        return false;
      }

      if (available >= capacity) {
        // Lapped. Jump to the oldest slot the writer is not currently inside and count
        // everything we stepped over.
        const oldestIntact = (writeSeq - capacity + 1) | 0;
        const skipped = sequenceDistance(
          asSequence(this.#readSeq),
          asSequence(oldestIntact),
        );
        this.#readSeq = oldestIntact;
        Atomics.add(this.#header, CONSUMER_DROPPED_AT, skipped);
        Atomics.store(this.#header, READ_SEQ_AT, this.#readSeq);
        continue;
      }

      const slotIndex = slotIndexFor(this.#layout, asSequence(this.#readSeq));
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- slotIndex is masked into 0..capacity-1 by construction, and #slots has exactly capacity entries.
      destination.set(this.#slots[slotIndex]!);

      // Did the writer reach into our slot while we copied? If so the bytes are a mix of two
      // frames and must be thrown away, not published.
      const writeSeqAfter = Atomics.load(this.#header, WRITE_SEQ_AT);
      if (
        sequenceDistance(asSequence(this.#readSeq), asSequence(writeSeqAfter)) >= capacity
      ) {
        continue;
      }

      this.#readSeq = (this.#readSeq + 1) | 0;
      Atomics.store(this.#header, READ_SEQ_AT, this.#readSeq);
      return true;
    }

    return false;
  }

  /** Slots the writer overwrote before this reader reached them. */
  get dropped(): number {
    return Atomics.load(this.#header, CONSUMER_DROPPED_AT) >>> 0;
  }

  /** Slots published but not yet read. May exceed capacity when the reader is lapped. */
  get pending(): number {
    return sequenceDistance(
      asSequence(this.#readSeq),
      asSequence(Atomics.load(this.#header, WRITE_SEQ_AT)),
    );
  }
}

/** Byte offset → the slot's first Uint32Array element index. */
export function wordIndexOf(byteOffset: number): number {
  return byteOffset >>> 2;
}

export type { ByteOffset };
